<template>
  <v-container fluid>
    <v-card class="mx-auto shared-image-card">
      <v-card-title>{{ sharedFile ? 'Geteiltes Bild' : 'Geteilte Datei' }}</v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          :type="sharedFile ? 'error' : 'warning'"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <div v-if="!isLoading && !sharedFile" class="mb-4">
          <p class="mb-3">Wähle die ursprüngliche Datei direkt aus. Danach kannst du wie gewohnt fortfahren.</p>
          <input ref="fallbackInput" type="file" accept="image/*,.pdf,application/pdf,.csv,text/csv" hidden @change="selectFallbackFile">
          <v-btn color="primary" @click="$refs.fallbackInput.click()">Datei auswählen</v-btn>
        </div>

        <details v-if="error && sharedRecord" class="mb-4">
          <summary>Diagnose zum Teilen</summary>
          <pre style="white-space: pre-wrap; overflow-wrap: anywhere">{{ shareDiagnostics }}</pre>
        </details>

        <v-alert
          v-if="uploadSuccess"
          type="success"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          Bild wurde hochgeladen.
        </v-alert>

        <div v-if="isLoading" class="py-6 text-center">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else-if="sharedFile">
          <v-img
            v-if="previewUrl"
            :src="previewUrl"
            alt="Geteiltes Bild"
            max-height="420"
            contain
            class="mb-4 shared-image-preview"
          />

          <div v-if="sharedFile" class="text-caption text-medium-emphasis mb-4">
            {{ sharedFile.name || 'geteiltes Bild' }} -
            {{ formatFileSize(sharedFile.size) }}
          </div>

          <v-alert
            v-if="uploadGeoInfo"
            :type="uploadGeoInfoType"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ uploadGeoInfo }}
          </v-alert>

          <div
            v-if="manualUploadCoordinates"
            class="manual-upload-coordinates mb-3"
          >
            Aktuelle Position:
            {{ formatCoordinate(manualUploadCoordinates.latitude) }},
            {{ formatCoordinate(manualUploadCoordinates.longitude) }}
          </div>

          <v-btn
            variant="tonal"
            color="secondary"
            size="small"
            class="mb-3"
            :loading="manualGeoLoading"
            :disabled="manualGeoLoading || uploadStarted"
            @click="useCurrentPositionForUpload"
          >
            Aktuelle Position verwenden
          </v-btn>

          <v-select
            v-model="selectedAssemblyId"
            :items="assemblyOptions"
            item-title="title"
            item-value="value"
            label="Assembly"
            density="compact"
            class="mb-3"
          />

          <v-text-field
            v-model="imageName"
            label="Bilder_Benennung"
            density="compact"
            class="mb-3"
          />
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn
          variant="text"
          @click="$router.push({ name: 'Alben' })"
        >
          Zu Alben
        </v-btn>

        <v-spacer />

        <v-btn
          v-if="sharedFile"
          color="primary"
          :loading="uploadStarted"
          :disabled="!sharedFile || !selectedAssemblyId || uploadStarted"
          @click="uploadSharedImage"
        >
          Hochladen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import axios from '@/api/client'
import { isPdfFile } from '@/utils/pdfFile'
import { formatImageUploadError } from '@/utils/imageUploadError'

const SHARE_DB_NAME = 'rateit-shared-images'
const SHARE_DB_VERSION = 1
const SHARE_STORE_NAME = 'incoming'

function openShareDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SHARE_DB_NAME, SHARE_DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(SHARE_STORE_NAME)) {
        db.createObjectStore(SHARE_STORE_NAME, { keyPath: 'id' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function getSharedRecord(shareId) {
  const db = await openShareDb()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(SHARE_STORE_NAME, 'readonly')
    const request = transaction.objectStore(SHARE_STORE_NAME).get(shareId)

    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

async function deleteSharedRecord(shareId) {
  const db = await openShareDb()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(SHARE_STORE_NAME, 'readwrite')
    transaction.objectStore(SHARE_STORE_NAME).delete(shareId)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

async function saveSharedRecord(record) {
  const db = await openShareDb()
  try {
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(SHARE_STORE_NAME, 'readwrite')
      transaction.objectStore(SHARE_STORE_NAME).put(record)
      transaction.oncomplete = resolve
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  } finally {
    db.close()
  }
}

export default {
  name: 'SharedImageView',

  data() {
    return {
      isLoading: true,
      uploadStarted: false,
      uploadSuccess: false,
      error: '',
      sharedRecord: null,
      sharedFile: null,
      previewUrl: '',
      selectedAssemblyId: null,
      imageName: '',
      manualGeoLoading: false,
      manualUploadCoordinates: null,
      uploadGeoInfo: '',
      uploadGeoInfoType: 'info'
    }
  },

  computed: {
    shareDiagnostics() {
      return JSON.stringify(this.sharedRecord?.diagnostics || {
        version: 'Älterer Teilen-Eingang ohne Diagnose',
        kind: this.sharedRecord?.kind,
        fileCount: this.sharedRecord?.files?.length || 0
      }, null, 2)
    },
    ...mapGetters({
      assemblies: 'stateAssemblies'
    }),

    assemblyOptions() {
      return [...(this.assemblies || [])]
        .sort((left, right) => Number(right.idassembly || 0) - Number(left.idassembly || 0))
        .map((assembly) => ({
          title: `${assembly.assembly_Benennung || 'Assembly'} (${assembly.idassembly})`,
          value: assembly.idassembly
        }))
    },

    selectedAssembly() {
      const selectedId = Number(this.selectedAssemblyId || 0)
      if (!selectedId) return null
      return (Array.isArray(this.assemblies) ? this.assemblies : [])
        .find(assembly => Number(assembly?.idassembly ?? assembly?.id ?? 0) === selectedId) || null
    }
  },

  async created() {
    await this.loadSharedImage()
  },

  watch: {
    async selectedAssemblyId() {
      await this.updateSharedImageGeoInfo()
    }
  },

  beforeUnmount() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
  },

  methods: {
    ...mapActions(['getAssemblies']),

    async selectFallbackFile(event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      this.isLoading = true
      this.error = ''
      try {
        if (!file.size) {
          this.error = 'Die ausgewählte Datei ist leer. Bitte wähle eine andere Datei.'
          return
        }
        const type = String(file.type || '').toLowerCase()
        const isPdf = await isPdfFile(file)
        const isCsv = /\.csv$/i.test(file.name || '') || ['text/csv', 'application/csv', 'application/vnd.ms-excel'].includes(type)
        if (!isPdf && !isCsv && !type.startsWith('image/')) {
          this.error = 'Bitte wähle ein Bild, eine PDF oder eine CSV-Datei.'
          return
        }
        const record = {
          ...this.sharedRecord,
          id: this.sharedRecord?.id || `share-${Date.now()}`,
          createdAt: new Date().toISOString(),
          title: file.name,
          kind: isPdf ? 'transaction-pdf' : isCsv ? 'transaction-csv' : 'image',
          files: [file]
        }
        await saveSharedRecord(record)
        this.sharedRecord = record
        const query = { shareId: record.id }
        if (isPdf || isCsv) {
          await this.$router.replace({ name: isPdf ? 'SharedTransactionPdf' : 'SharedTransactionCsv', query })
        } else {
          await this.$router.replace({ name: 'SharedImage', query })
          await this.loadSharedImage()
        }
      } catch (error) {
        this.error = error.message || 'Die Datei konnte nicht übernommen werden. Bitte versuche es erneut.'
      } finally {
        this.isLoading = false
      }
    },

    async loadSharedImage() {
      this.isLoading = true
      this.error = ''

      try {
        const shareId = String(this.$route.query.shareId || '').trim()
        if (!shareId) {
          this.error = 'Kein geteiltes Bild gefunden.'
          return
        }

        const record = await getSharedRecord(shareId)
        this.sharedRecord = record
        for (const candidate of record?.files || []) {
          if (await isPdfFile(candidate)) {
            await this.$router.replace({ name: 'SharedTransactionPdf', query: { shareId } })
            return
          }
        }
        const file = record?.files?.[0] || null

        if (!file || !file.size) {
          this.error = record
            ? 'Die Datei konnte beim Teilen nicht übernommen werden. Bitte wähle sie hier erneut aus.'
            : 'Die geteilte Datei ist nicht mehr verfügbar. Bitte erneut teilen.'
          return
        }
        if (!String(file.type || '').toLowerCase().startsWith('image/')) {
          this.error = 'Eine Datei ist angekommen, wurde aber nicht als PDF, CSV oder Bild erkannt. Bitte die Diagnose zum Teilen prüfen.'
          return
        }

        this.sharedRecord = record
        this.sharedFile = file
        this.previewUrl = URL.createObjectURL(file)
        this.imageName = record.title || file.name || ''
        await this.getAssemblies()
        await this.updateSharedImageGeoInfo()
      } catch (error) {
        console.error(error)
        this.error = 'Das geteilte Bild konnte nicht geladen werden.'
      } finally {
        this.isLoading = false
      }
    },

    async uploadSharedImage() {
      if (!this.sharedFile || !this.selectedAssemblyId) return

      this.uploadStarted = true
      this.uploadSuccess = false
      this.error = ''

      try {
        const { latitude, longitude, source, message } = await this.resolveUploadCoordinates(this.sharedFile)
        const formData = new FormData()
        formData.append('file', this.sharedFile)
        formData.append('assembly_id', this.selectedAssemblyId)
        if (this.hasCoordinates(latitude, longitude)) {
          formData.append('bilder_geo_longi_X', longitude)
          formData.append('bilder_geo_lati_Y', latitude)
        }
        formData.append('Bilder_Benennung', this.imageName || this.sharedFile.name || '')

        await this.$store.dispatch('createBildmitfiles', formData)

        if (this.sharedRecord?.id) {
          await deleteSharedRecord(this.sharedRecord.id)
        }

        this.uploadSuccess = true
        this.uploadGeoInfo = message
        this.uploadGeoInfoType = source === 'none' || source === 'server-exif' ? 'info' : 'success'
        this.manualUploadCoordinates = null
      } catch (error) {
        console.error(error)
        this.error = formatImageUploadError(error)
      } finally {
        this.uploadStarted = false
      }
    },

    formatFileSize(size) {
      const numberValue = Number(size)
      if (!Number.isFinite(numberValue)) return ''

      return `${(numberValue / 1024 / 1024).toFixed(2)} MB`
    },

    toCoordinateNumber(value) {
      if (value === null || value === undefined || value === '') return null
      const numberValue = Number(String(value).replace(',', '.'))
      return Number.isFinite(numberValue) ? numberValue : null
    },

    hasCoordinates(latitude, longitude) {
      return (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        !(latitude === 0 && longitude === 0) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180
      )
    },

    formatCoordinate(value) {
      const numberValue = this.toCoordinateNumber(value)
      return Number.isFinite(numberValue) ? numberValue.toFixed(6) : '-'
    },

    getCoordinatesFromObject(item) {
      if (!item || typeof item !== 'object') return null

      const latitude = this.toCoordinateNumber(
        item.bilder_geo_lati_Y ??
        item.assembly_geo_lati_Y ??
        item.geo_lati_Y ??
        item.latitude ??
        item.lat ??
        item.assembly?.bilder_geo_lati_Y ??
        item.assembly?.geo_lati_Y ??
        item.assembly?.latitude ??
        item.assembly?.lat
      )
      const longitude = this.toCoordinateNumber(
        item.bilder_geo_longi_X ??
        item.assembly_geo_longi_X ??
        item.geo_longi_X ??
        item.longitude ??
        item.lon ??
        item.lng ??
        item.assembly?.bilder_geo_longi_X ??
        item.assembly?.geo_longi_X ??
        item.assembly?.longitude ??
        item.assembly?.lon ??
        item.assembly?.lng
      )

      return this.hasCoordinates(latitude, longitude)
        ? { latitude, longitude }
        : null
    },

    async getSelectedAssemblyCoordinates() {
      const directCoordinates = this.getCoordinatesFromObject(this.selectedAssembly)
      if (directCoordinates) return directCoordinates

      const selectedId = Number(this.selectedAssemblyId || 0)
      if (!selectedId) return null

      try {
        const { data } = await axios.get(`/assemblies/${selectedId}/media`)
        const media = Array.isArray(data) ? data : []
        for (const item of media) {
          const coordinates = this.getCoordinatesFromObject(item)
          if (coordinates) return coordinates
        }
      } catch (error) {
        console.error(error)
      }

      return null
    },

    async useCurrentPositionForUpload() {
      this.manualGeoLoading = true

      try {
        const coordinates = await this.getCurrentPositionOnce()

        if (!this.hasCoordinates(coordinates.latitude, coordinates.longitude)) {
          this.uploadGeoInfo = 'Browser-Geolocation hat keine gueltigen Koordinaten geliefert.'
          this.uploadGeoInfoType = 'warning'
          return
        }

        this.manualUploadCoordinates = coordinates
        this.uploadGeoInfo = `Aktuelle Position wird beim Upload verwendet: ${this.formatCoordinate(coordinates.latitude)}, ${this.formatCoordinate(coordinates.longitude)}.`
        this.uploadGeoInfoType = 'success'
      } catch (error) {
        console.error(error)
        this.uploadGeoInfo = 'Aktuelle Position konnte nicht ermittelt werden. Browser-Freigabe pruefen.'
        this.uploadGeoInfoType = 'warning'
      } finally {
        this.manualGeoLoading = false
      }
    },

    getCurrentPositionOnce() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by this browser'))
          return
        }

        navigator.geolocation.getCurrentPosition(
          position => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            })
          },
          error => reject(error),
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          }
        )
      })
    },

    isMobileUploadDevice() {
      const userAgent = String(navigator.userAgent || '').toLowerCase()
      const hasTouch = navigator.maxTouchPoints && navigator.maxTouchPoints > 1

      return (
        /android|iphone|ipad|ipod|mobile/.test(userAgent) ||
        (hasTouch && Math.min(window.innerWidth, window.innerHeight) <= 900)
      )
    },

    async updateSharedImageGeoInfo() {
      if (!this.sharedFile) {
        this.uploadGeoInfo = ''
        this.uploadGeoInfoType = 'info'
        return
      }

      const exifCoordinates = await this.getExifCoordinates(this.sharedFile)

      if (exifCoordinates) {
        this.uploadGeoInfo = `EXIF-GPS gefunden: ${this.formatCoordinate(exifCoordinates.latitude)}, ${this.formatCoordinate(exifCoordinates.longitude)}. Diese Koordinaten werden beim Upload verwendet.`
        this.uploadGeoInfoType = 'success'
        return
      }

      if (this.manualUploadCoordinates) {
        this.uploadGeoInfo = `Keine EXIF-GPS-Koordinaten gefunden. Aktuelle Position per Button gewaehlt: ${this.formatCoordinate(this.manualUploadCoordinates.latitude)}, ${this.formatCoordinate(this.manualUploadCoordinates.longitude)}.`
        this.uploadGeoInfoType = 'success'
        return
      }

      const assemblyCoordinates = await this.getSelectedAssemblyCoordinates()
      if (assemblyCoordinates) {
        this.uploadGeoInfo = `Keine EXIF-GPS-Koordinaten gefunden. Beim Upload werden die GPS-Koordinaten des ausgewaehlten Assembly verwendet: ${this.formatCoordinate(assemblyCoordinates.latitude)}, ${this.formatCoordinate(assemblyCoordinates.longitude)}.`
        this.uploadGeoInfoType = 'info'
        return
      }

      this.uploadGeoInfo = 'Keine EXIF-GPS-Koordinaten gefunden. Aktuelle Position kann per Button verwendet werden; ansonsten werden keine Geo-Koordinaten gesendet.'
      this.uploadGeoInfoType = 'info'
    },

    async resolveUploadCoordinates(uploadFile) {
      const exifCoordinates = await this.getExifCoordinates(uploadFile)

      if (exifCoordinates) {
        return {
          ...exifCoordinates,
          source: 'exif',
          message: `EXIF-GPS gefunden und verwendet: ${this.formatCoordinate(exifCoordinates.latitude)}, ${this.formatCoordinate(exifCoordinates.longitude)}.`
        }
      }

      if (this.manualUploadCoordinates) {
        return {
          ...this.manualUploadCoordinates,
          source: 'manual-browser',
          message: `Keine EXIF-GPS-Koordinaten gefunden. Manuell gewaehlte aktuelle Position verwendet: ${this.formatCoordinate(this.manualUploadCoordinates.latitude)}, ${this.formatCoordinate(this.manualUploadCoordinates.longitude)}.`
        }
      }

      const assemblyCoordinates = await this.getSelectedAssemblyCoordinates()
      if (assemblyCoordinates) {
        return {
          ...assemblyCoordinates,
          source: 'assembly',
          message: `Keine EXIF-GPS-Koordinaten gefunden. GPS-Koordinaten des ausgewaehlten Assembly verwendet: ${this.formatCoordinate(assemblyCoordinates.latitude)}, ${this.formatCoordinate(assemblyCoordinates.longitude)}.`
        }
      }

      return {
        latitude: null,
        longitude: null,
        source: 'none',
        message: 'Keine EXIF-GPS-Koordinaten gefunden, keine manuelle Position gewaehlt und keine Assembly-GPS-Koordinaten vorhanden. Es wurden keine Geo-Koordinaten gesendet.'
      }
    },

    async getExifCoordinates(file) {
      if (!file) return null

      try {
        const buffer = await file.arrayBuffer()
        return this.parseExifGpsCoordinates(buffer)
      } catch (error) {
        console.error(error)
        return null
      }
    },

    parseExifGpsCoordinates(buffer) {
      const view = new DataView(buffer)

      if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) {
        return null
      }

      let offset = 2

      while (offset + 4 < view.byteLength) {
        if (view.getUint8(offset) !== 0xff) return null

        const marker = view.getUint8(offset + 1)
        const segmentLength = view.getUint16(offset + 2, false)

        if (marker === 0xe1 && this.isExifSegment(view, offset + 4)) {
          return this.readExifGpsFromSegment(view, offset + 10)
        }

        offset += 2 + segmentLength
      }

      return null
    },

    isExifSegment(view, offset) {
      return (
        view.getUint8(offset) === 0x45 &&
        view.getUint8(offset + 1) === 0x78 &&
        view.getUint8(offset + 2) === 0x69 &&
        view.getUint8(offset + 3) === 0x66 &&
        view.getUint8(offset + 4) === 0x00 &&
        view.getUint8(offset + 5) === 0x00
      )
    },

    readExifGpsFromSegment(view, tiffStart) {
      const littleEndian =
        view.getUint8(tiffStart) === 0x49 &&
        view.getUint8(tiffStart + 1) === 0x49

      if (view.getUint16(tiffStart + 2, littleEndian) !== 42) return null

      const firstIfdOffset = view.getUint32(tiffStart + 4, littleEndian)
      const gpsIfdOffset = this.findGpsIfdOffset(view, tiffStart, firstIfdOffset, littleEndian)
      if (!gpsIfdOffset) return null

      return this.readGpsIfd(view, tiffStart, gpsIfdOffset, littleEndian)
    },

    findGpsIfdOffset(view, tiffStart, ifdOffset, littleEndian) {
      const ifdStart = tiffStart + ifdOffset
      const entryCount = view.getUint16(ifdStart, littleEndian)

      for (let index = 0; index < entryCount; index += 1) {
        const entryOffset = ifdStart + 2 + (index * 12)
        const tag = view.getUint16(entryOffset, littleEndian)

        if (tag === 0x8825) {
          return view.getUint32(entryOffset + 8, littleEndian)
        }
      }

      return null
    },

    readGpsIfd(view, tiffStart, gpsIfdOffset, littleEndian) {
      const gpsIfdStart = tiffStart + gpsIfdOffset
      const entryCount = view.getUint16(gpsIfdStart, littleEndian)
      let latitudeRef = ''
      let longitudeRef = ''
      let latitudeValues = null
      let longitudeValues = null

      for (let index = 0; index < entryCount; index += 1) {
        const entryOffset = gpsIfdStart + 2 + (index * 12)
        const tag = view.getUint16(entryOffset, littleEndian)

        if (tag === 0x0001) latitudeRef = this.readExifAscii(view, entryOffset, littleEndian)
        if (tag === 0x0002) latitudeValues = this.readExifRationals(view, tiffStart, entryOffset, littleEndian)
        if (tag === 0x0003) longitudeRef = this.readExifAscii(view, entryOffset, littleEndian)
        if (tag === 0x0004) longitudeValues = this.readExifRationals(view, tiffStart, entryOffset, littleEndian)
      }

      if (!latitudeValues || !longitudeValues) return null

      const latitude = this.convertGpsCoordinate(latitudeValues, latitudeRef)
      const longitude = this.convertGpsCoordinate(longitudeValues, longitudeRef)

      return this.hasCoordinates(latitude, longitude)
        ? { latitude, longitude }
        : null
    },

    readExifAscii(view, entryOffset, littleEndian) {
      const count = view.getUint32(entryOffset + 4, littleEndian)
      const valueOffset = entryOffset + 8
      let value = ''

      for (let index = 0; index < Math.min(count, 4); index += 1) {
        const charCode = view.getUint8(valueOffset + index)
        if (charCode) value += String.fromCharCode(charCode)
      }

      return value
    },

    readExifRationals(view, tiffStart, entryOffset, littleEndian) {
      const count = view.getUint32(entryOffset + 4, littleEndian)
      const valueOffset = tiffStart + view.getUint32(entryOffset + 8, littleEndian)
      const values = []

      for (let index = 0; index < count; index += 1) {
        const rationalOffset = valueOffset + (index * 8)
        const numerator = view.getUint32(rationalOffset, littleEndian)
        const denominator = view.getUint32(rationalOffset + 4, littleEndian)
        values.push(denominator ? numerator / denominator : 0)
      }

      return values
    },

    convertGpsCoordinate(values, ref) {
      const coordinate = values[0] + (values[1] / 60) + (values[2] / 3600)
      return ['S', 'W'].includes(ref?.trim()) ? -coordinate : coordinate
    }
  }
}
</script>

<style scoped>
.shared-image-card {
  max-width: 760px;
}

.shared-image-preview {
  background: rgba(255, 255, 255, 0.04);
}

.manual-upload-coordinates {
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.86rem;
}
</style>
